import React from "react";
import { StatCard } from "@/components/ui/StatCard";
import { Sparkline } from "@/components/ui/Sparkline";
import { useAlertsOverview } from "@/hooks/useCryptoAlerts";

interface AlertsStatsRowProps {
  onUpcomingClick?: () => void;
  /** lg: the desktop row of three equal tiles. */
  size?: "md" | "lg";
  className?: string;
}

/**
 * Alerts page stat tiles over the last 7 days, from `GET /crypto-alerts/overview`:
 * new listings (with the change against the week before and a daily trend),
 * listings now trading well above their alert price, and upcoming listings.
 */
export const AlertsStatsRow: React.FC<AlertsStatsRowProps> = ({
  onUpcomingClick,
  size = "md",
  className = "",
}) => {
  const { data: overview } = useAlertsOverview();
  const lg = size === "lg";
  const change = overview?.listings.changePercent;
  const tileClass = lg ? "min-w-0 flex-1" : "";

  return (
    <div
      className={
        lg
          ? `flex gap-4 ${className}`
          : `-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] ${className}`
      }
    >
      <StatCard
        size={size}
        tone="green"
        icon="/icons/figma/funds-line.svg"
        value={overview ? overview.listings.count : "–"}
        label="New Listings"
        className={tileClass}
        title="Listings announced in the last 7 days, and the change against the 7 days before"
        aside={
          overview && (
            <div className="flex shrink-0 flex-col items-center justify-between gap-1">
              {change != null && (
                <span
                  className={`rounded-full border px-2 text-xs font-medium leading-[18px] ${
                    change >= 0
                      ? "border-[#00653b] bg-[#002c14] text-[#56d799]"
                      : "border-[#7f1d1d] bg-[#2a0a0a] text-[#f87171]"
                  }`}
                >
                  {change >= 0 ? "+" : ""}
                  {Math.round(change)}%
                </span>
              )}
              <Sparkline values={overview.listings.daily} width={40} height={22} />
            </div>
          )
        }
      />
      <StatCard
        size={size}
        tone="orange"
        icon="/icons/figma/fire-line.svg"
        value={overview ? overview.hot.count : "–"}
        label="Hot Opportunities"
        className={tileClass}
        title={
          overview
            ? `Listings from the last 7 days that ran ${overview.hot.thresholdPercent}%+ above their alert price`
            : undefined
        }
      />
      <button
        type="button"
        onClick={onUpcomingClick}
        className={`shrink-0 text-left ${lg ? "flex min-w-0 flex-1" : ""}`}
      >
        <StatCard
          size={size}
          tone="purple"
          icon="/icons/figma/time-line.svg"
          value={overview ? overview.upcoming : "–"}
          label="Upcoming"
          className={lg ? "w-full" : ""}
          title="Announced listings that haven't opened yet (last 7 days)"
        />
      </button>
    </div>
  );
};
